import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions } from '@microsoft/signalr';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { UserNotificationService } from './user-notification.service';

@UntilDestroy()
@Injectable()
export class SignalrService implements OnDestroy {
    private hubConnection: HubConnection;
    private onCloseCallback: Function;
    private wasConnectionLost: boolean = false;

    constructor(
        private authService: AuthenticationService,
        private ngZone: NgZone,
        private userNotificationService: UserNotificationService,
    ) {}

    ngOnDestroy(): void {
        this.stop();
    }

    startConnection(url: string, eventName: string, callback: (val: unknown) => void) {
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(url, <IHttpConnectionOptions>{
                accessTokenFactory: () => this.authService.accessToken,
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true,
            })
            .withAutomaticReconnect([
                0, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000, 2000,
                2000, 2000, 2000, 2000, 2000, 2000, 2000, 10000, 30000, 60000, 90000, 120000, 150000, 180000,
            ])
            .build();

        this.hubConnection.onreconnecting(error => {
            // when we deploy new notification service instance we do not want to disturb user with error message
            // because we should be connected to the new notification service in a couple of seconds again and that
            // is why we wait for 10 seconds before showing the error if not connected to the new service
            setTimeout(() => {
                if (this.hubConnection?.state !== HubConnectionState.Connected) {
                    this.userNotificationService.showError('GENERAL.WEBSOCKET_CONNECTION_LOST');
                    this.wasConnectionLost = true;
                    console.error('Web socket connection lost: ' + error);
                }
            }, 10000);
        });

        this.hubConnection.onreconnected(() => {
            if (this.wasConnectionLost) {
                this.userNotificationService.showInfo('GENERAL.WEBSOCKET_CONNECTION_REESTABLISHED');
                this.wasConnectionLost = false;
            }
        });

        this.onCloseCallback = error => {
            this.userNotificationService.showError('GENERAL.WEBSOCKET_CLOSED');
            console.error('Error while reconnecting: ' + error);
        };
        this.hubConnection.onclose(connectionId => this.onCloseCallback(connectionId));

        this.ngZone.runOutsideAngular(() => {
            this.start(this.hubConnection);
        });

        const dataEvent$$: BehaviorSubject<unknown> = new BehaviorSubject(undefined);
        this.hubConnection.on(eventName, (data: unknown) => {
            dataEvent$$.next(data);
        });

        dataEvent$$
            .asObservable()
            .pipe(
                tap((event: unknown) => {
                    this.ngZone.run(() => callback(event));
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    stop(): void {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            // remove onclose event handler to avoid showing error when this method is called
            // to stop connection (could not use off() method here since it does not work)
            this.onCloseCallback = () => {
                // do nothing
            };
            this.hubConnection.stop();
        }
    }

    private async start(hubConnection: HubConnection, retryCounter: number = 0) {
        try {
            await hubConnection.start();
            if (retryCounter > 0) {
                this.userNotificationService.showInfo('GENERAL.WEBSOCKET_CONNECTION_ESTABLISHED');
            }
        } catch (err) {
            // do not show error to the user every time when trying to start connection again
            // show it first time and then every 5 minutes since there is retry timeout of 5 seconds
            if (retryCounter % 60 === 0) {
                this.userNotificationService.showError('GENERAL.WEBSOCKET_CONNECTION_FAILURE');
                console.error('Error while starting connection: ' + err);
            }
            retryCounter++;
            setTimeout(() => {
                this.start(hubConnection, retryCounter);
            }, 5000);
        }
    }
}
